import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import './index.css';
import { router } from './router.tsx';
import { FullScreenLoader } from '@/features/ui/components/FullScreenLoader';
import { logger } from '@/features/help/utils/logger';
import { initFeatureFlags } from '@/features/feature-flags/services/feature-flags.service';

// Initialize Firebase Remote Config feature flags on app startup.
// This runs asynchronously and doesn't block the app from loading.
// Feature flags will use default values until Remote Config is fetched.
initFeatureFlags().then((success) => {
  logger.info('Main', 'Feature flags initialization complete', { success });
});

// Global unhandled Promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  const isTimeoutError = 
    (typeof error === 'string' && error.toLowerCase().includes('timeout')) ||
    (error instanceof Error && error.message.toLowerCase().includes('timeout'));

  logger.error('Global Handler', 'Unhandled Promise rejection detected', {
    reason: error,
    isTimeoutError,
    reasonType: typeof error,
    stack: error instanceof Error ? error.stack : undefined,
    url: window.location.href,
    timestamp: new Date().toISOString(),
  });

  // Prevent the browser from logging the error to console if it's a timeout
  // (since we've already logged it properly)
  if (isTimeoutError) {
    event.preventDefault();
  }
});

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>
);
